import type { ReactNode } from 'react';

import { cn } from '../../lib/utils';

export interface WidgetGridLayoutProps {
  children: ReactNode;
  className?: string;
  'data-testid'?: string;
}

/**
 * Presentational 12-column grid for laying out card content. Consumers
 * typically wrap this with data orchestration at the app layer; storybook
 * and ui consumers can use it directly with mock content.
 *
 * Vertical alignment is intentionally left to the caller via `className`
 * (e.g. `items-start` for compact cards sized to their own content,
 * `items-stretch` for equal-height rows). Defaulting to a specific value
 * here would silently constrain future consumers.
 *
 * Always 12 columns, never a narrower count: cards place themselves with an
 * inline `gridColumn: "<col> / span <n>"`, which on a 1-column grid spawns
 * implicit columns and stacks cards on top of each other. A caller that needs
 * a floor width passes it as a `min-w-*` class, and its scroll container then
 * scrolls sideways below it.
 */
export function WidgetGridLayout({
  children,
  className,
  'data-testid': dataTestId,
}: WidgetGridLayoutProps) {
  return (
    <div className={cn('grid grid-cols-12 gap-5', className)} data-testid={dataTestId}>
      {children}
    </div>
  );
}

WidgetGridLayout.displayName = 'WidgetGridLayout';
